<script setup lang="ts">
/**
 * Where toasts appear (SPEC.md §10.4, §11.6, §11.7).
 *
 * Bottom right, above everything, one stack. `role="status"` with `aria-live="polite"` so a screen
 * reader announces the result of a mutation without interrupting what the operator is typing —
 * §11.7's rule that status is never conveyed by presentation alone applies to sound as much as to
 * colour, which is why each toast also carries an icon and a word, not just a tint.
 */
import { AlertCircle, CheckCircle2, Info, Undo2, X } from '@lucide/vue'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { useToast } from '@/shared/composables/useToast'

const { t } = useI18n()
const { toasts, dismiss } = useToast()

const ICONS = { success: CheckCircle2, error: AlertCircle, info: Info }
const TONES = {
  success: 'border-positive/40 bg-positive/5 text-positive',
  error: 'border-danger/40 bg-danger/5 text-danger',
  info: 'border-border bg-surface-2 text-ink-600',
}

/** Guards against a double-click firing the reversal twice. */
const undoing = ref<number | null>(null)

async function runUndo(id: number, undo: () => Promise<unknown>): Promise<void> {
  if (undoing.value !== null) return
  undoing.value = id
  try {
    await undo()
    dismiss(id)
  } finally {
    undoing.value = null
  }
}
</script>

<template>
  <Teleport to="body">
    <div
      class="pointer-events-none fixed right-4 bottom-4 z-[60] flex w-full max-w-sm flex-col gap-2"
      role="status"
      aria-live="polite"
    >
      <TransitionGroup name="toast">
        <div
          v-for="toast in toasts"
          :key="toast.id"
          class="bg-surface-1 pointer-events-auto flex items-start gap-3 rounded-[10px] border p-3 shadow-sm"
          :class="TONES[toast.tone]"
        >
          <component :is="ICONS[toast.tone]" class="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <p class="text-ink-900 flex-1 text-sm">{{ toast.message }}</p>

          <button
            v-if="toast.undo"
            type="button"
            class="text-accent-600 flex shrink-0 items-center gap-1 text-sm font-medium hover:underline disabled:opacity-50"
            :disabled="undoing === toast.id"
            @click="runUndo(toast.id, toast.undo)"
          >
            <Undo2 class="size-3.5" aria-hidden="true" />
            {{ toast.undoLabel ?? t('common.undo') }}
          </button>

          <button
            type="button"
            class="text-ink-400 hover:text-ink-900 shrink-0"
            :aria-label="t('common.close')"
            @click="dismiss(toast.id)"
          >
            <X class="size-3.5" aria-hidden="true" />
          </button>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<style scoped>
.toast-enter-active,
.toast-leave-active {
  transition: all 150ms ease-out;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

@media (prefers-reduced-motion: reduce) {
  .toast-enter-active,
  .toast-leave-active {
    transition: none;
  }
}
</style>
